import React, { useState, useEffect } from 'react';
import { 
  X, 
  TrendingUp, 
  Mail, 
  Settings, 
  ClipboardList, 
  Calendar, 
  DollarSign, 
  ShoppingBag, 
  User, 
  Plus, 
  Trash2, 
  RotateCw,
  CheckCircle2,
  AlertCircle,
  TrendingDown,
  Users,
  Layers,
  ShieldCheck,
  Send,
  Server,
  Inbox,
  Sparkles,
  Image as ImageIcon,
  ChevronRight,
  Bot
} from 'lucide-react';
import { OrderRecord, OrderStatus, UserRole, Product } from '../types';
import { db, collection, getDocs, secureSetDoc, doc } from '../firebase';
import { TransparentImageLibrary } from './TransparentImageLibrary';
import { ProductInventoryList } from './ProductInventoryList';
import { ChatAndShopSettingsTab } from './ChatAndShopSettingsTab';
import { formatThaiDateTime } from '../lib/dateUtils';

interface SellerDashboardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectOrderForSlip: (order: OrderRecord) => void;
  availableProducts?: Product[];
}

export const SellerDashboardModal: React.FC<SellerDashboardModalProps> = ({
  isOpen,
  onClose,
  onSelectOrderForSlip,
  availableProducts = []
}) => {
  const [activeTab, setActiveTab] = useState<'analytics' | 'orders' | 'chatandshop' | 'settings' | 'roles' | 'products' | 'transparent'>('analytics');
  const [orders, setOrders] = useState<OrderRecord[]>([]);
  const [analytics, setAnalytics] = useState<any>(null);
  const [adminEmails, setAdminEmails] = useState<string[]>([]);
  const [emailStatus, setEmailStatus] = useState<any>(null);
  const [testingEmail, setTestingEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [usersList, setUsersList] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [selectedProductForTransparent, setSelectedProductForTransparent] = useState<string | undefined>(undefined);


  const fetchUsers = async () => {
    try {
      const qSnap = await getDocs(collection(db, 'users'));
      const list: any[] = [];
      qSnap.forEach((d) => {
        list.push({ id: d.id, ...d.data() });
      });
      setUsersList(list);
    } catch (e) {
      console.error('Error fetching users:', e);
    }
  };

  const handleRoleChange = async (userId: string, newRole: UserRole) => {
    try {
      const userRef = doc(db, 'users', userId);
      const targetUser = usersList.find(u => u.id === userId);
      if (targetUser) {
        const updatedUser = { ...targetUser, role: newRole };
        await secureSetDoc(userRef, updatedUser, `users/${userId}`);
        setSuccessMsg(`อัปเดตสิทธิ์ของ ${targetUser.email} เป็น "${newRole}" เรียบร้อยแล้ว!`);
        fetchUsers();
      }
    } catch (err) {
      console.error('Role update failed:', err);
      setErrorMsg('เกิดข้อผิดพลาดในการเปลี่ยนสิทธิ์ผู้ใช้งาน');
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      // 1. Fetch Orders
      const orderRes = await fetch('/api/orders');
      if (orderRes.ok) {
        const orderData = await orderRes.json();
        setOrders(orderData.orders || []);
      }

      // 2. Fetch Analytics
      const analyticsRes = await fetch('/api/admin/analytics');
      if (analyticsRes.ok) {
        const analyticsData = await analyticsRes.json();
        setAnalytics(analyticsData);
      }

      // 3. Fetch Admin Emails & SMTP Status
      const emailsRes = await fetch('/api/admin/emails');
      if (emailsRes.ok) {
        const emailsData = await emailsRes.json();
        setAdminEmails(emailsData.emails || []);
      }

      const statusRes = await fetch('/api/admin/email-status');
      if (statusRes.ok) {
        const statusData = await statusRes.json();
        setEmailStatus(statusData);
      }
    } catch (err) {
      console.error('Failed to load seller dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestEmail = async (target?: string) => {
    try {
      setTestingEmail(true);
      setErrorMsg('');
      const targetEmail = target || adminEmails[0] || 'giffarine108037654@gmail.com';
      const res = await fetch('/api/admin/test-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ targetEmail }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccessMsg(`ส่งอีเมลทดสอบการแจ้งเตือนไปยัง ${targetEmail} เรียบร้อยแล้ว!`);
        // Refresh email status & outbox logs
        const statusRes = await fetch('/api/admin/email-status');
        if (statusRes.ok) {
          setEmailStatus(await statusRes.json());
        }
      } else {
        setErrorMsg(data.error || 'เกิดข้อผิดพลาดในการทดสอบส่งอีเมล');
      }
    } catch (err: any) {
      setErrorMsg(err.message || 'เกิดข้อผิดพลาดในการทดสอบส่งอีเมล');
    } finally {
      setTestingEmail(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDashboardData();
      fetchUsers();
      setSuccessMsg('');
      setErrorMsg('');
    }
  }, [isOpen, activeTab]);

  if (!isOpen) return null;

  const handleUpdateStatus = async (orderId: string, newStatus: OrderStatus) => {
    try {
      setUpdatingId(orderId);
      const res = await fetch(`/api/orders/${orderId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          orderStatus: newStatus,
          paymentStatus: newStatus === 'Completed' || newStatus === 'Shipped' ? 'Paid' : undefined,
          trackingNumber: newStatus === 'Shipped' ? `TH${Date.now().toString().slice(-9)}` : undefined,
        }),
      });
      if (res.ok) {
        await fetchDashboardData();
        setSuccessMsg('อัปเดตสถานะคำสั่งซื้อสำเร็จ!');
      }
    } catch (err) {
      console.error('Status update failed:', err);
      setErrorMsg('ไม่สามารถอัปเดตสถานะได้');
    } finally {
      setUpdatingId(null);
    }
  };

  const handleAddEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newEmail || !newEmail.includes('@')) {
      setErrorMsg('กรุณากรอกรูปแบบอีเมลให้ถูกต้อง');
      return;
    }

    if (adminEmails.includes(newEmail)) {
      setErrorMsg('อีเมลนี้ได้รับการลงทะเบียนแจ้งเตือนแล้ว');
      return;
    }

    const updatedEmails = [...adminEmails, newEmail];
    try {
      const res = await fetch('/api/admin/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: updatedEmails }),
      });
      if (res.ok) {
        setAdminEmails(updatedEmails);
        setNewEmail('');
        setSuccessMsg('เพิ่มอีเมลรับการแจ้งเตือนสำเร็จ!');
      }
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการบันทึกการตั้งค่า');
    }
  };

  const handleDeleteEmail = async (emailToDelete: string) => {
    const updatedEmails = adminEmails.filter(e => e !== emailToDelete);
    try {
      const res = await fetch('/api/admin/emails', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emails: updatedEmails }),
      });
      if (res.ok) {
        setAdminEmails(updatedEmails);
        setSuccessMsg('ลบอีเมลแจ้งเตือนสำเร็จ!');
      }
    } catch (err) {
      setErrorMsg('เกิดข้อผิดพลาดในการลบการตั้งค่า');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs p-3 sm:p-6 overflow-y-auto">
      <div className="relative w-full max-w-6xl my-auto bg-white rounded-2xl shadow-2xl overflow-hidden border border-slate-200 flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-blue-100 bg-gradient-to-r from-[#0E2C58] to-[#1E3A8A] text-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 text-white flex items-center justify-center font-bold">
              <Settings className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black text-white">
                ระบบผู้ดูแลร้านค้ากิฟฟารีน (Seller & Admin Dashboard)
              </h2>
              <p className="text-xs text-blue-100">
                วิเคราะห์ยอดขาย ตั้งค่ารับการแจ้งเตือนอีเมล และจัดการคำสั่งซื้อลูกค้าอย่างปลอดภัย
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Responsive Side Menu Navigation Layout */}
        <div className="flex flex-col md:flex-row flex-1 min-h-0 overflow-hidden">
          {/* Side Menu Navigation */}
          <aside className="w-full md:w-64 border-b md:border-b-0 md:border-r border-slate-200 bg-slate-50/70 p-2 sm:p-3 shrink-0 flex md:flex-col gap-1.5 overflow-x-auto md:overflow-y-auto">
            {/* 1. Analytics */}
            <button
              type="button"
              onClick={() => setActiveTab('analytics')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'analytics'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <TrendingUp className="w-4 h-4 shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">รายงานวิเคราะห์ยอดขาย</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'analytics' ? 'text-white' : 'text-slate-300'}`} />
            </button>

            {/* 2. Orders */}
            <button
              type="button"
              onClick={() => setActiveTab('orders')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'orders'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <ClipboardList className="w-4 h-4 shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">รายการซื้อของลูกค้า</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'orders' ? 'text-white' : 'text-slate-300'}`} />
            </button>

            {/* 3. Settings */}
            <button
              type="button"
              onClick={() => setActiveTab('settings')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'settings'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Mail className="w-4 h-4 shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">ตั้งค่ารับเมล์แจ้งเตือน</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'settings' ? 'text-white' : 'text-slate-300'}`} />
            </button>

            {/* 4. Roles */}
            <button
              type="button"
              onClick={() => setActiveTab('roles')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'roles'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <ShieldCheck className="w-4 h-4 shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">จัดการสิทธิ์บทบาท</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'roles' ? 'text-white' : 'text-slate-300'}`} />
            </button>

            {/* 4.5 Products */}
            <button
              type="button"
              onClick={() => setActiveTab('products')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'products'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <ShoppingBag className="w-4 h-4 text-emerald-500 shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">คลังสินค้า</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'products' ? 'text-white' : 'text-slate-300'}`} />
            </button>

            {/* 5. Transparent */}
            <button
              type="button"
              onClick={() => setActiveTab('transparent')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'transparent'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <ImageIcon className="w-4 h-4 text-amber-500 shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">คลังภาพไลฟ์</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'transparent' ? 'text-white' : 'text-slate-300'}`} />
            </button>

            {/* 6. Chat & Shop */}
            <button
              type="button"
              onClick={() => setActiveTab('chatandshop')}
              className={`p-2 sm:p-2.5 rounded-xl text-left transition-all flex items-center justify-between gap-2.5 whitespace-nowrap shrink-0 md:shrink md:w-full cursor-pointer ${
                activeTab === 'chatandshop'
                  ? 'bg-gradient-to-r from-[#2A59FE] via-[#3B66FE] to-[#4F46E5] text-white shadow-xs font-bold'
                  : 'text-slate-700 hover:bg-blue-50 hover:text-[#2A59FE]'
              }`}
            >
              <div className="flex items-center gap-2 min-w-0">
                <Bot className="w-4 h-4 text-[#2A59FE] shrink-0" />
                <span className="text-xs sm:text-sm font-bold truncate">Chat & Shop (AI)</span>
              </div>
              <ChevronRight className={`hidden md:block w-3.5 h-3.5 ${activeTab === 'chatandshop' ? 'text-white' : 'text-slate-300'}`} />
            </button>
          </aside>

          {/* Content Body */}
          <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-slate-50/20">
          {successMsg && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-2 text-emerald-700 text-xs font-semibold">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span>{successMsg}</span>
            </div>
          )}

          {errorMsg && (
            <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl flex items-center gap-2 text-rose-700 text-xs font-semibold">
              <AlertCircle className="w-4 h-4 text-rose-600" />
              <span>{errorMsg}</span>
            </div>
          )}

          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center gap-2 text-[#2A59FE]">
              <RotateCw className="w-8 h-8 animate-spin" />
              <span className="text-xs text-slate-500 font-bold">กำลังประมวลผลรายงานระบบ...</span>
            </div>
          ) : activeTab === 'analytics' ? (
            /* Tab 1: Sales Performance Analytics */
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Daily performance */}
                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-3xs">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-xs text-slate-500 font-bold">ยอดขายประจำวันนี้ (Daily)</span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-[#2A59FE] text-[10px] font-bold">Today</span>
                  </div>
                  <div className="text-2xl font-black text-slate-900 mb-1">
                    ฿{analytics?.daily?.sales?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-slate-400">
                    รายการทั้งหมด: <strong className="text-slate-700 font-bold">{analytics?.daily?.count || 0} รายการ</strong>
                  </div>
                </div>

                {/* Monthly performance */}
                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-3xs">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-xs text-slate-500 font-bold">ยอดขายประจำเดือนนี้ (Monthly)</span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-[#2A59FE] text-[10px] font-bold">Month</span>
                  </div>
                  <div className="text-2xl font-black text-slate-900 mb-1">
                    ฿{analytics?.monthly?.sales?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-slate-400">
                    รายการทั้งหมด: <strong className="text-slate-700 font-bold">{analytics?.monthly?.count || 0} รายการ</strong>
                  </div>
                </div>

                {/* Yearly performance */}
                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-3xs">
                  <div className="flex justify-between items-start mb-3">
                    <span className="text-xs text-slate-500 font-bold">ยอดขายรวมตลอดปี (Yearly)</span>
                    <span className="px-2 py-0.5 rounded-full bg-blue-50 text-[#2A59FE] text-[10px] font-bold">Year</span>
                  </div>
                  <div className="text-2xl font-black text-slate-900 mb-1">
                    ฿{analytics?.yearly?.sales?.toLocaleString() || '0'}
                  </div>
                  <div className="text-xs text-slate-400">
                    รายการทั้งหมด: <strong className="text-slate-700 font-bold">{analytics?.yearly?.count || 0} รายการ</strong>
                  </div>
                </div>
              </div>

              {/* Performance Graphics and trends block */}
              <div className="bg-white p-6 rounded-2xl border border-blue-100 shadow-3xs">
                <h3 className="text-sm font-black text-[#0E2C58] mb-4 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                  <span>สรุปภาพรวมเปรียบเทียบแนวโน้มการเติบโต</span>
                </h3>
                
                <div className="space-y-4">
                  {/* Daily Progress */}
                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-600 mb-1.5">
                      <span>เป้าหมายรายวัน (฿15,000)</span>
                      <span>{Math.min(100, Math.round(((analytics?.daily?.sales || 0) / 15000) * 100))}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-blue-400 to-[#2A59FE]"
                        style={{ width: `${Math.min(100, ((analytics?.daily?.sales || 0) / 15000) * 100)}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Monthly Progress */}
                  <div>
                    <div className="flex justify-between text-xs font-bold text-slate-600 mb-1.5">
                      <span>เป้าหมายรายเดือน (฿150,000)</span>
                      <span>{Math.min(100, Math.round(((analytics?.monthly?.sales || 0) / 150000) * 100))}%</span>
                    </div>
                    <div className="w-full h-2.5 bg-slate-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-teal-400 to-emerald-600"
                        style={{ width: `${Math.min(100, ((analytics?.monthly?.sales || 0) / 150000) * 100)}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : activeTab === 'orders' ? (
            /* Tab 2: Orders History Table Report */
            <div className="bg-white rounded-2xl border border-blue-100 shadow-3xs overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-50 text-slate-600 border-b border-blue-100 font-extrabold text-[11px]">
                    <tr>
                      <th className="p-3.5">ID คำสั่งซื้อ</th>
                      <th className="p-3.5">วันเวลาที่สั่งซื้อ</th>
                      <th className="p-3.5">ชื่อลูกค้า</th>
                      <th className="p-3.5">เบอร์โทรศัพท์</th>
                      <th className="p-3.5">ยอดรวมสุทธิ</th>
                      <th className="p-3.5">วิธีชำระ</th>
                      <th className="p-3.5">สถานะใบสั่งซื้อ</th>
                      <th className="p-3.5 text-right">การจัดการ</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {orders.map((o) => (
                      <tr key={o.orderId} className="hover:bg-slate-50/50 transition-colors">
                        <td className="p-3.5 font-mono font-bold text-[#2A59FE]">
                          {o.orderId}
                        </td>
                        <td className="p-3.5 text-slate-500 font-mono text-[11.5px]">
                          {formatThaiDateTime(o.timestamp)}
                        </td>
                        <td className="p-3.5 font-bold text-slate-800">
                          {o.customerName}
                        </td>
                        <td className="p-3.5 font-mono text-slate-600">{o.phone}</td>
                        <td className="p-3.5 font-black text-slate-900">
                          ฿{o.totalAmount.toLocaleString()}
                        </td>
                        <td className="p-3.5">
                          <span className={`px-2 py-0.5 rounded-md text-[10.5px] font-bold ${
                            o.paymentMethod === 'BANK_TRANSFER'
                              ? 'bg-blue-50 text-blue-700'
                              : 'bg-amber-50 text-amber-700'
                          }`}>
                            {o.paymentMethod}
                          </span>
                        </td>
                        <td className="p-3.5">
                          <select
                            value={o.orderStatus}
                            onChange={(e) =>
                              handleUpdateStatus(o.orderId, e.target.value as OrderStatus)
                            }
                            disabled={updatingId === o.orderId}
                            className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 font-bold text-[#0E2C58] focus:outline-none focus:border-[#2A59FE]"
                          >
                            <option value="Wait_Verification">Wait_Verification</option>
                            <option value="Pending_Shipment">Pending_Shipment</option>
                            <option value="Processing">Processing</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </td>
                        <td className="p-3.5 text-right whitespace-nowrap">
                          <button
                            onClick={() => onSelectOrderForSlip(o)}
                            className="px-2.5 py-1.5 bg-blue-50 hover:bg-blue-100 text-[#0E2C58] font-bold rounded-lg text-[10.5px] transition-colors"
                          >
                            เปิดสลิปใบเสร็จ
                          </button>
                        </td>
                      </tr>
                    ))}
                    {orders.length === 0 && (
                      <tr>
                        <td colSpan={8} className="p-8 text-center text-slate-400 font-bold">
                          ไม่พบรายการคำสั่งซื้อของลูกค้าในระบบขณะนี้
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ) : activeTab === 'settings' ? (
            /* Tab 3: Admin Settings for Email Alerts & SMTP Architecture */
            <div className="space-y-6">
              {/* SMTP Architecture Status Card */}
              <div className="bg-gradient-to-br from-blue-50/70 via-white to-slate-50 p-5 rounded-2xl border border-blue-100 shadow-3xs">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-blue-100/60">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#0E2C58] text-white flex items-center justify-center font-bold shadow-xs">
                      <Server className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-sm font-black text-[#0E2C58] flex items-center gap-2">
                        ระบบเซิร์ฟเวอร์แจ้งเตือนอีเมลอัตโนมัติ (Email SMTP Gateway)
                        {emailStatus?.isSmtpConfigured ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                            <CheckCircle2 className="w-3 h-3" /> เชื่อมต่อ SMTP Gmail แล้ว
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                            <AlertCircle className="w-3 h-3" /> โหมดพร้อมเชื่อมต่อ (Ready for SMTP_PASS)
                          </span>
                        )}
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">
                        ระบบประมวลผลคำสั่งซื้อและส่งอีเมลแจ้งเตือนแบบ Real-time ทุกครั้งที่มีการสั่งซื้อใหม่
                      </p>
                    </div>
                  </div>

                  {/* Test Email Button */}
                  <button
                    onClick={() => handleSendTestEmail()}
                    disabled={testingEmail}
                    className="px-4 py-2 bg-gradient-to-r from-[#0E2C58] to-[#2A59FE] hover:from-[#0b2142] hover:to-blue-700 text-white font-bold text-xs rounded-xl shadow-xs flex items-center justify-center gap-2 transition-all active:scale-95 disabled:opacity-50 shrink-0"
                  >
                    <Send className={`w-3.5 h-3.5 ${testingEmail ? 'animate-spin' : ''}`} />
                    <span>{testingEmail ? 'กำลังส่งทดสอบ...' : 'ส่งอีเมลทดสอบระบบแจ้งเตือน'}</span>
                  </button>
                </div>

                {/* SMTP Setup Notice */}
                <div className="mt-4 p-3.5 bg-white/80 rounded-xl border border-blue-100/80 text-xs text-slate-600 space-y-1.5">
                  <div className="font-bold text-[#0E2C58] flex items-center gap-1.5">
                    <Inbox className="w-4 h-4 text-[#2A59FE]" />
                    <span>คำแนะนำการรับอีเมลเข้ากล่องข้อความ Gmail (Inbox) โดยตรง:</span>
                  </div>
                  <p className="text-[11.5px] leading-relaxed text-slate-600">
                    1. เข้าไปที่บัญชี Google บัญชีแอดมิน <strong className="text-slate-800">giffarine108037654@gmail.com</strong> &gt; ความปลอดภัย (Security) &gt; ยืนยันแบบ 2 ขั้นตอน &gt; รหัสผ่านสำหรับแอป (App passwords)<br />
                    2. สร้าง App Password 16 หลัก แล้วนำมาใส่ใน <strong className="text-slate-800">Settings &gt; Secrets</strong> ในชื่อตัวแปร <code className="bg-blue-50 text-blue-700 px-1 py-0.5 rounded font-mono font-bold">SMTP_PASS</code><br />
                    3. อีเมลแจ้งเตือนคำสั่งซื้อทุกออเดอร์จะถูกส่งตรงเข้ากล่องข้อความของแอดมินทันที 100%
                  </p>
                </div>
              </div>

              {/* Add target alert email form & Recipient Lists */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                {/* Form */}
                <form onSubmit={handleAddEmail} className="md:col-span-1 bg-white p-5 rounded-2xl border border-blue-100 shadow-3xs space-y-4">
                  <h3 className="text-sm font-black text-[#0E2C58] border-b border-blue-50 pb-2 mb-2">
                    เพิ่มอีเมลผู้รับการแจ้งเตือน
                  </h3>
                  <p className="text-xs text-slate-500">
                    ป้อนอีเมลของเจ้าหน้าที่หรือแอดมิน เพื่อส่งข้อความรายงานคำสั่งซื้อใหม่โดยทันที
                  </p>
                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                      อีเมลแอดมิน <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="email"
                      required
                      placeholder="admin@yourdomain.com"
                      value={newEmail}
                      onChange={(e) => setNewEmail(e.target.value)}
                      className="w-full px-4 py-2.5 rounded-xl border border-slate-200 focus:border-[#2A59FE] focus:outline-none text-sm"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-[#2A59FE] text-white font-bold text-xs sm:text-sm rounded-xl flex items-center justify-center gap-1.5 shadow-sm active:scale-98 hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span>เพิ่มเป้าหมายแจ้งเตือน</span>
                  </button>
                </form>

                {/* Recipient Lists */}
                <div className="md:col-span-2 bg-white p-5 rounded-2xl border border-blue-100 shadow-3xs">
                  <h3 className="text-sm font-black text-[#0E2C58] border-b border-blue-50 pb-2 mb-3">
                    รายชื่อผู้รับเมล์แจ้งเตือนในระบบ ({adminEmails.length})
                  </h3>
                  {adminEmails.length === 0 ? (
                    <p className="text-xs text-slate-400 py-6 text-center">ไม่มีแอดมินรับการแจ้งเตือน กรุณาเพิ่มที่ฟอร์มด้านซ้าย</p>
                  ) : (
                    <div className="divide-y divide-slate-100">
                      {adminEmails.map((email) => (
                        <div key={email} className="flex items-center justify-between py-3.5">
                          <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 rounded-full bg-blue-50 text-[#2A59FE] flex items-center justify-center">
                              <Mail className="w-4 h-4" />
                            </div>
                            <span className="text-xs sm:text-sm font-bold text-slate-700">{email}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleSendTestEmail(email)}
                              disabled={testingEmail}
                              className="px-2.5 py-1 text-[11px] font-bold text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors flex items-center gap-1"
                              title="ส่งอีเมลทดสอบมายังอีเมลนี้"
                            >
                              <Send className="w-3 h-3" />
                              <span>ทดสอบส่ง</span>
                            </button>
                            <button
                              onClick={() => handleDeleteEmail(email)}
                              className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors"
                              title="ลบรายชื่อนี้"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Outgoing Email Logs History */}
              {emailStatus?.recentLogs && emailStatus.recentLogs.length > 0 && (
                <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-3xs">
                  <h3 className="text-sm font-black text-[#0E2C58] border-b border-blue-50 pb-2 mb-3 flex items-center justify-between">
                    <span>ประวัติบันทึกการส่งการแจ้งเตือนล่าสุด ({emailStatus.recentLogs.length} รายการ)</span>
                    <span className="text-[11px] font-normal text-slate-400">อัปเดตแบบ Real-time</span>
                  </h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="bg-slate-50 text-slate-500 font-bold border-b border-slate-100">
                          <th className="p-2.5">เวลาที่ส่ง</th>
                          <th className="p-2.5">ผู้รับ (To)</th>
                          <th className="p-2.5">หัวข้ออีเมล (Subject)</th>
                          <th className="p-2.5 text-right">สถานะ</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {emailStatus.recentLogs.slice(0, 10).map((log: any) => (
                          <tr key={log.id} className="hover:bg-slate-50/50">
                            <td className="p-2.5 text-slate-500 font-mono text-[11px]">{formatThaiDateTime(log.timestamp)}</td>
                            <td className="p-2.5 font-bold text-slate-700">{log.to}</td>
                            <td className="p-2.5 text-slate-600 max-w-[240px] truncate">{log.subject}</td>
                            <td className="p-2.5 text-right">
                              {log.status === 'sent' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-emerald-50 text-emerald-700">
                                  <CheckCircle2 className="w-3 h-3" /> ส่งสำเร็จ
                                </span>
                              ) : log.status === 'queued_no_smtp' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-amber-50 text-amber-700" title="ประมวลผลแล้ว (ใส่ SMTP_PASS เพื่อส่งตรงเข้า Gmail)">
                                  <AlertCircle className="w-3 h-3" /> บันทึกออกแล้ว (รอใส่ App Password)
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10.5px] font-bold bg-rose-50 text-rose-700" title={log.error}>
                                  <AlertCircle className="w-3 h-3" /> ล้มเหลว
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* Tab 4: User Role Architecture Management */
            <div className="space-y-6">
              {/* Role Architecture Guide Card */}
              <div className="bg-gradient-to-br from-blue-50/70 via-white to-[#F8FAFC] p-5 sm:p-6 rounded-2xl border border-blue-100 shadow-xs">
                <div className="flex items-center gap-2 mb-4">
                  <Layers className="w-5 h-5 text-[#0E2C58]" />
                  <h3 className="text-base font-black text-[#0E2C58]" id="role-architecture-title">
                    สถาปัตยกรรมกำหนดสิทธิ์การเข้าถึงข้อมูลระบบ (User RBAC Architecture)
                  </h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* Customer Role Guide */}
                  <div className="bg-white p-4 rounded-xl border border-blue-100 shadow-3xs hover:border-blue-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-lg bg-blue-100 text-[#2A59FE] flex items-center justify-center font-bold text-xs">
                        1
                      </div>
                      <span className="text-sm font-black text-slate-800">Regular Customer / Member</span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      <strong>สิทธิ์การใช้งานปกติ:</strong> สามารถสั่งซื้อสินค้ากิฟฟารีนด้วยราคาสมาชิก, สะสมคะแนนสะสม (PV), แก้ไขข้อมูลที่อยู่จัดส่งพัสดุ และตรวจสอบประวัติคำสั่งซื้อส่วนบุคคล
                    </p>
                    <div className="mt-3.5 inline-flex items-center gap-1 text-[10px] font-bold text-[#2A59FE] bg-blue-50 px-2 py-0.5 rounded">
                      <span>สถานะ: ลูกค้าทั่วไป</span>
                    </div>
                  </div>

                  {/* Seller Role Guide */}
                  <div className="bg-white p-4 rounded-xl border border-amber-100 shadow-3xs hover:border-amber-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center font-bold text-xs">
                        2
                      </div>
                      <span className="text-sm font-black text-slate-800">Seller / Store Owner</span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      <strong>สิทธิ์ตัวแทนจำหน่าย:</strong> สำหรับตัวแทนพาร์ทเนอร์และผู้จำหน่าย เข้าถึงข้อมูลรายงานยอดขายรวมรายวัน/เดือน, ปรับปรุงสถานะออเดอร์ของลูกค้า และตั้งค่าเลขขนส่งพัสดุ
                    </p>
                    <div className="mt-3.5 inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 px-2 py-0.5 rounded">
                      <span>สถานะ: ตัวแทนจำหน่าย</span>
                    </div>
                  </div>

                  {/* Admin/Developer Role Guide */}
                  <div className="bg-white p-4 rounded-xl border border-indigo-100 shadow-3xs hover:border-indigo-300 transition-colors">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-xs">
                        3
                      </div>
                      <span className="text-sm font-black text-slate-800">Admin / Developer</span>
                    </div>
                    <p className="text-[11px] text-slate-500 leading-relaxed">
                      <strong>สิทธิ์ผู้ดูแลระบบหลัก:</strong> ควบคุมข้อมูลการทำงานของแอปพลิเคชันทั้งหมด, จัดการสิทธิ์การเข้าถึงของสมาชิกแต่ละบัญชี, ซิงก์ข้อมูล และเพิ่มอีเมลพนักงานเป้าหมายในการรับข่าวสาร
                    </p>
                    <div className="mt-3.5 inline-flex items-center gap-1 text-[10px] font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded">
                      <span>สถานะ: ผู้ดูแลระบบสูงสุด</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Roles Modification Table */}
              <div className="bg-white p-5 rounded-2xl border border-blue-100 shadow-xs">
                <div className="flex items-center justify-between border-b border-blue-50 pb-3 mb-4">
                  <div>
                    <h3 className="text-sm font-black text-[#0E2C58]">
                      ตารางจัดการสิทธิ์ผู้ใช้งานในฐานข้อมูล ({usersList.length} บัญชี)
                    </h3>
                    <p className="text-xs text-slate-500">
                      เปลี่ยนบทบาทของพนักงานเพื่อจำลองการแยกสิทธิ์การทำงานในหน้าระบบร้านค้า
                    </p>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-slate-700">
                    <thead className="bg-slate-50 text-slate-500 uppercase font-bold text-[10px] tracking-wider">
                      <tr>
                        <th className="p-3">รหัสสมาชิก (Customer ID)</th>
                        <th className="p-3">อีเมลเข้าใช้งาน</th>
                        <th className="p-3">วันที่สมัครระบบ</th>
                        <th className="p-3">สิทธิ์ในระบบปัจจุบัน</th>
                        <th className="p-3 text-right">ปรับปรุงบทบาทสิทธิ์</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {usersList.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="p-4 text-center text-slate-400">
                            ไม่พบบัญชีผู้ใช้งานที่บันทึกในฐานข้อมูล
                          </td>
                        </tr>
                      ) : (
                        usersList.map((usr) => (
                          <tr key={usr.id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="p-3 font-mono font-bold text-[#2A59FE]">
                              {usr.customerId || 'ไม่มี (รอตั้ง PIN)'}
                            </td>
                            <td className="p-3 font-medium text-slate-800">{usr.email}</td>
                            <td className="p-3 text-slate-500">
                              {usr.createdAt ? new Date(usr.createdAt).toLocaleDateString('th-TH') : 'ไม่มีข้อมูล'}
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-extrabold ${
                                usr.role === 'admin' 
                                  ? 'bg-indigo-50 text-indigo-700 border border-indigo-100' 
                                  : usr.role === 'seller' 
                                    ? 'bg-amber-50 text-amber-700 border border-amber-100' 
                                    : 'bg-blue-50 text-blue-700 border border-blue-100'
                              }`}>
                                {usr.role === 'admin' && 'ผู้ดูแลสูงสุด (Admin)'}
                                {usr.role === 'seller' && 'ตัวแทนจำหน่าย (Seller)'}
                                {usr.role === 'customer' && 'ลูกค้าสมาชิก (Customer)'}
                                {!usr.role && 'ไม่ได้กำหนด'}
                              </span>
                            </td>
                            <td className="p-3 text-right">
                              <select
                                id={`role-select-${usr.id}`}
                                value={usr.role || 'customer'}
                                onChange={(e) => handleRoleChange(usr.id, e.target.value as UserRole)}
                                className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 focus:border-[#2A59FE] focus:outline-none cursor-pointer"
                              >
                                <option value="customer">ลูกค้าสมาชิก (Customer)</option>
                                <option value="seller">ตัวแทนจำหน่าย (Seller)</option>
                                <option value="admin">ผู้ดูแลระบบสูงสุด (Admin)</option>
                              </select>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Tab 4.5: Products (คลังสินค้า) */}
          {activeTab === 'products' && (
            <div className="space-y-4">
              <ProductInventoryList 
                availableProducts={availableProducts} 
                onSelectForTransparent={(code) => {
                  setSelectedProductForTransparent(code);
                  setActiveTab('transparent');
                }}
              />
            </div>
          )}

          {/* Tab 5: Transparent Asset Library (คลังภาพไลฟ์) */}
          {activeTab === 'transparent' && (
            <div className="space-y-4">
              <TransparentImageLibrary 
                availableProducts={availableProducts} 
                initialSelectedProductCode={selectedProductForTransparent}
              />
            </div>
          )}

          {/* Tab 6: Chat & Shop (AI Agent Settings & Tickets) */}
          {activeTab === 'chatandshop' && (
            <div className="space-y-4">
              <ChatAndShopSettingsTab />
            </div>
          )}
          </div>
        </div>
      </div>
    </div>
  );
};
